import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import ConfirmModal from './ConfirmModal';
import { API_URL } from '../config';
import './PostList.css';

function PostList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const { getAuthHeader } = useAuth();

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/admin/all`, {
        headers: getAuthHeader()
      });
      setPosts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch posts');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (id, title) => {
    setPostToDelete({ id, title });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;

    try {
      await axios.delete(`${API_URL}/posts/${postToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Post deleted successfully');
      setShowDeleteModal(false);
      setPostToDelete(null);
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
      setShowDeleteModal(false);
      setPostToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  const filteredPosts = posts.filter(post => {
    if (filter === 'all') return true;
    return post.status === filter;
  });

  if (loading) {
    return <div className="loading">Loading posts...</div>;
  }

  return (
    <div className="post-list main-content">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">All Posts</h1>
          <Link to="/posts/new" className="btn-primary create-post-btn">
            + Create New Post
          </Link>
        </div>

        <div className="filters">
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All ({posts.length})
          </button>
          <button 
            className={filter === 'published' ? 'active' : ''} 
            onClick={() => setFilter('published')}
          >
            Published ({posts.filter(p => p.status === 'published').length})
          </button>
          <button 
            className={filter === 'draft' ? 'active' : ''} 
            onClick={() => setFilter('draft')}
          >
            Drafts ({posts.filter(p => p.status === 'draft').length})
          </button>
        </div>

        {filteredPosts.length === 0 ? (
          <div className="card">
            <p style={{textAlign: 'center', color: '#666'}}>
              No posts found. <Link to="/posts/new">Create your first post</Link>
            </p>
          </div>
        ) : (
          <div className="posts-table-container">
            <table className="posts-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Media</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosts.map(post => (
                  <tr key={post.id}>
                    <td>
                      <div className="post-title-cell">
                        <strong>{post.title}</strong>
                        {post.excerpt && (
                          <small>{post.excerpt.substring(0, 80)}...</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        backgroundColor: post.media_type === 'video' ? '#e3f2fd' : '#f3e5f5',
                        color: post.media_type === 'video' ? '#1976d2' : '#7b1fa2'
                      }}>
                        {post.media_type === 'video' ? '🎥 Video' : '🖼️ Image'}
                      </span>
                    </td>
                    <td>{post.category}</td>
                    <td>
                      <span className={`status-badge ${post.status}`}>
                        {post.status}
                      </span>
                    </td>
                    <td>{post.published_date ? new Date(post.published_date).toLocaleDateString() : new Date(post.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/posts/edit/${post.id}`} 
                          className="btn-secondary btn-sm"
                        >
                          Edit
                        </Link>
                        <button 
                          onClick={() => handleDeleteClick(post.id, post.title)}
                          className="btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Post"
        message={`Are you sure you want to delete "${postToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}

export default PostList;
